/* eslint-disable max-len */
import React from 'react'
import classnames from 'classnames'
import { compose } from 'redux'
import {
  makeForm,
  RadioFormField,
  BaseComponent,
  TextFormField,
  SelectDropdownFormField,
  MaskedFormField,
  NumberFormField,
} from 'belle-js'
import {  
  MASK_PATTERN, 
} from 'utils/fieldMasking'
import styles from './AccountDetails.module.scss'
import formStyles from 'components/Onboarding/FormStyles.module.scss'
import withFormResponseHandler from '../../withFormResponseHandler'
import {
  SCHWAB_IRA,
  SCHWAB_CHARITABLE,
  SCHWAB_ONE_PERSONAL,
} from 'utils/financialAccountTypes.js'
import {
  firstNameValidation,
  middleNameValidation,
  lastNameValidation,
  birthdateValidation,
  donorDobConfirmationValidation,
  ssnValidation,
  cityValidation,
  postalCodeValidation,
  stateValidation,
  phoneNumberValidation,
  minorDobConfirmationValidation,
  minorSsnConfirmationValidation,
  deceasedDobConfirmationValidation,
  deceasedSsnConfirmationValidation,
  dateOfDeathValidation,
  fundNameValidation,
  donorSsnConfirmationValidation,
} from 'utils/fieldValidations'
import { countryList } from 'utils/fieldOptions'

const getFormattedRadioLabel = texts => (
  <div className={styles.enrollmentOptionParent}>
    { texts.map((text, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <p key={index}>{text}</p>
    ))}
  </div>
)

const paperlessEnrollmentTypes = [{
  label: getFormattedRadioLabel(['Yes', 'Paperless, please!']),
  value: 'Yes',
}, {
  label: getFormattedRadioLabel(['No', 'Please send all documents via postal mail']),
  value: 'No',
}]

const additionalDonorTypes = [{
  label: 'Yes',
  value: 'Yes',
}, {
  label: 'No',
  value: 'No',
}]

class AccountDetailsForm extends BaseComponent {

  constructor (props) {
    super(props)

    this.state = {
      didBirthdateChange: false,
      isSsnBeingEdited: false,
      didSsnChange: false,
    }

    this.handleClick = this.handleClick.bind(this)
    this.isInheritedIRA = this.isInheritedIRA.bind(this)
    this.handleSsnFocus = this.handleSsnFocus.bind(this)
    this.handleSsnBlur = this.handleSsnBlur.bind(this)
    this.handleSsnChange = this.handleSsnChange.bind(this)
    this.handleBirthdateChange = this.handleBirthdateChange.bind(this)
    this.hasCustodian = this.hasCustodian.bind(this)
    this.getLastFourDigitSsn = this.getLastFourDigitSsn.bind(this)
  }

  getLastFourDigitSsn (isDonorAdvisedFund, hasCustodian) {
    const { initialData } = this.props

    if (isDonorAdvisedFund) {
      return initialData.donorSsnLastFour
    } else if (hasCustodian) {
      return initialData.minorSsnLastFour
    } else {
      return initialData.deceasedSsnLastFour
    }
  }

  handleClick () {
    const { openModal } = this.props
    openModal('paperlessInfoModal')
  }

  isInheritedIRA () {
    const { accountSubType } = this.props
    return accountSubType === SCHWAB_IRA.ACCOUNT_TYPES.INHERITED_IRA
  }

  hasCustodian () {
    const { accountSubType } = this.props
    const { CUSTODIAL_ROTH_IRA, CUSTODIAL_IRA } = SCHWAB_IRA.ACCOUNT_TYPES
    const { UTMA } = SCHWAB_ONE_PERSONAL.ACCOUNT_TYPES
    return [CUSTODIAL_ROTH_IRA, CUSTODIAL_IRA, UTMA].includes(accountSubType)
  }

  handleSsnFocus () {
    this.setState({ isSsnBeingEdited: true })
  }

  handleSsnBlur (ssnType) {
    const { formData } = this.props

    if (formData[ssnType] === '') {
      this.setState({ isSsnBeingEdited: false })
    }
  }

  handleSsnChange (value, ssnType) {
    const { initialData } = this.props

    this.setState({
      didSsnChange: (
        value
          ? initialData[ssnType] !== value
          : !!initialData[ssnType]
      )
    })
  }

  handleBirthdateChange (value, dobType) {
    const { initialData } = this.props
    this.setState({
      didBirthdateChange: (
        value 
          ? initialData[dobType] !== value
          : !!initialData[dobType]
      )
    })
  }

  render () {
    const {
      formValid,
      onClick,
      formData,
      accountConfig,
      initialData,
      accountSubType,
      isMobile,
    } = this.props
    const {
      didBirthdateChange,
      isSsnBeingEdited,
      didSsnChange,
    } = this.state

    const primaryButtonClasses = classnames(
      'button',
      'button--primary',
      styles.submitButton,
      !formValid ? 'disabled' : null
    )

    const {
      fields: {
        accountHolderFormHeader,
        accountHolderFormName,
        accountHolderFormLastName,
        jointAccountFormName,
        jointAccountFormLastName,
        paperlessFormName,
        anotherDonor,
        accountTypeHeader,
        accountTypeValue,
        jointAccountFormHeader,
        donorAdvisedFundName,
      }
    } = accountConfig

    const firstName = initialData[`${accountHolderFormName}`] || ''
    const lastName = initialData[`${accountHolderFormLastName}`] || ''
    const fullName = `${firstName} ${lastName}`.trim()
    const isDonorAdvisedFund = accountSubType === SCHWAB_CHARITABLE.ACCOUNT_TYPES.DONOR_ADVISED_FUND

    const jointFirstName = initialData[`${jointAccountFormName}`] || ''
    const jointLastName = initialData[`${jointAccountFormLastName}`] || ''
    const jointFullName = `${jointFirstName} ${jointLastName}`.trim()

    const paperLessQuestion = 'Would you like to enroll this account in Paperless Documents?'

    const hasAdditionalDonor = formData[anotherDonor] === 'Yes'
    const isInheritedIRA = this.isInheritedIRA()
    const hasCustodian = this.hasCustodian()
    const lastFourDigitSsn = this.getLastFourDigitSsn(isDonorAdvisedFund, hasCustodian)
    const isSSNMasked = !isSsnBeingEdited && lastFourDigitSsn
    const inheritedIRASectionStyle = classnames(styles.twoColumnSection, styles.marginYsm)

    return (
      <div className={styles.Form}>
        <div className={styles.section}>
          <div className={styles.formInput}>
            <p className={styles.heading}>{accountTypeHeader}</p>
            <TextFormField
              name="typeOfAccount"
              label={accountTypeHeader}
              type="text"
              placeholder={accountTypeHeader}
              value={accountTypeValue}
              readOnly={true}
              className={styles.accountTypeFormField} />
          </div>
        </div>
        <div className={styles.twoColumnSection}>
          <div className={styles.formInput}>
            <p className={styles.heading}>{accountHolderFormHeader}</p>
            <TextFormField
              name="accountHolderFullName"
              label={accountHolderFormHeader}
              type="text"
              placeholder={accountHolderFormHeader}
              value={fullName}
              readOnly={true}
              className={styles.formField} />
          </div>
          { jointFullName && (
            <div className={styles.formInput}>
              <p className={styles.heading}>{jointAccountFormHeader}</p>
              <TextFormField
                name="jointAccountHolderFullName"
                label={jointAccountFormHeader}
                type="text"
                placeholder={jointAccountFormHeader}
                value={jointFullName}
                readOnly={true}
                className={styles.formField} />
            </div>
          ) }
        </div>
        <div className={styles.paperlessDocumentEnrollment}>
          <p className={styles.heading}>Paperless Document Enrollment</p>
          <span className={styles.question}>
            {paperLessQuestion}
            <a className={styles.moreSection} onClick={this.handleClick}> MORE </a>
          </span>
          <div>
            <RadioFormField
              label="Paperless Enrollment"
              className={styles.paperlessRadio}
              name={paperlessFormName}
              horizontal={false}
              block={false}
              options={paperlessEnrollmentTypes}
              required={true}
              value={formData[`${paperlessFormName}`]} />
          </div>
        </div>

        {
          isDonorAdvisedFund &&
            <div>
              <div className={styles.donorAdvisedFund}>
                <p className={styles.heading}>Additional Donor</p>
                <span className={styles.question}>
                  Is there another donor?
                </span>
                <div>
                  <RadioFormField
                    label="Additional Donor"
                    className={styles.paperlessRadio}
                    name={anotherDonor}
                    horizontal={false}
                    block={false}
                    options={additionalDonorTypes}
                    required={true}
                    value={formData[anotherDonor]} />
                </div>
              </div>
              {
                hasAdditionalDonor &&
                  <div className={formStyles.Form}>
                    <div className={formStyles.formSection}>
                      <p className={formStyles.heading}>Full name</p>
                      <TextFormField
                        name="donorFirstName"
                        label="First Name"
                        className={formStyles.FormFieldInSection}
                        placeholder="First Name"
                        validations={firstNameValidation.rules}
                        validationErrors={firstNameValidation.messages}
                        maxLength={35}
                        value={formData.donorFirstName || ''}
                        required={true}
                        testId="accountDetails__donorFirstName__field" />
        
                      <TextFormField
                        name="donorMiddleName"
                        label="Middle Name"
                        className={formStyles.FormFieldInSection}
                        placeholder="Middle Name"
                        validations={middleNameValidation.rules}
                        validationErrors={middleNameValidation.messages}
                        maxLength={35}
                        value={formData.donorMiddleName || ''}
                        required={false}
                        testId="accountDetails__donorMiddleName__field" />
        
                      <TextFormField
                        name="donorLastName"
                        label="Last Name"
                        className={formStyles.FormField}
                        maxLength={35}
                        placeholder="Last name"
                        validations={lastNameValidation.rules}
                        validationErrors={lastNameValidation.messages}
                        value={formData.donorLastName || ''}
                        testId="accountDetails__donorLastName__field"
                        required={true} />
                    </div>
                    <div className={formStyles.formSection}>
                      <p className={formStyles.heading}>Date of birth</p>
                      <div className={formStyles.twoColumn}>
                        <MaskedFormField
                          name="donorDateOfBirth"
                          label="Date of Birth"
                          value={formData.donorDateOfBirth}
                          validations={birthdateValidation.rules}
                          className={formStyles.FormField}
                          validationErrors={birthdateValidation.messages}
                          placeholder="MM/DD/YYYY"
                          mask="date"
                          testId="accountDetails__donorDateOfBirth__field"
                          required={true}
                          onChange={value => this.handleBirthdateChange(value, 'donorDateOfBirth')} />
                        {(didBirthdateChange || !formData.donorDateOfBirth) &&
                        <MaskedFormField
                          name="donorDateOfBirthConfirmation"
                          label="Date of Birth Confirmation"
                          value={formData.donorDateOfBirthConfirmation}
                          validations={
                            didBirthdateChange ? donorDobConfirmationValidation.rules : {}
                          }
                          className={formStyles.FormField}
                          validationErrors={donorDobConfirmationValidation.messages}
                          placeholder="Confirm MM/DD/YYYY"
                          mask="date"
                          testId="accountDetails__donorDateOfBirthConfirmation__field" />}
                      </div>
                    </div>
                    <div className={formStyles.formSection}>
                      <p className={formStyles.heading} >Social Security Number</p>
                      <div className={formStyles.twoColumn}>
                        <MaskedFormField
                          name="donorSocialSecurityNumber"
                          label="Social Security Number"
                          placeholder="123-45-6789"
                          className={styles.FormField}
                          validations={isSSNMasked ? undefined : { isValidSocialSecurityNumber: true }}
                          validationErrors={isSSNMasked ? undefined : ssnValidation.messages}
                          testId="accountDetails__donorSocialSecurityNumber__field"
                          required={false}
                          value={isSSNMasked ? lastFourDigitSsn : ''}
                          mask={isSSNMasked ? MASK_PATTERN : 'ssn'}
                          onBlur={() => this.handleSsnBlur('donorSocialSecurityNumber')}
                          onFocus={this.handleSsnFocus}
                          onChange={value => this.handleSsnChange(value, 'donorSocialSecurityNumber')} />
                        {(didSsnChange || !lastFourDigitSsn) &&
                          <MaskedFormField
                            name="ssnConfirmation"
                            label="SSN Confirmation"
                            placeholder="Confirm SSN"
                            className={styles.FormField}
                            validations={{ isValidDonorSsnMatch: isSsnBeingEdited }}
                            mask={isSSNMasked ? MASK_PATTERN : 'ssn'}
                            validationErrors={donorSsnConfirmationValidation.messages}
                            testId="accountDetails__donorSsnConfirmation__field"
                            required={isSsnBeingEdited}  />
                        }
                      </div>
                    </div>
                    <div className={formStyles.formSection}>
                      <p className={formStyles.heading}>Mailing Address</p>
                      <div className={formStyles.twoColumn}>
                        <div>
                          <TextFormField
                            name="donorAddress"
                            placeholder="Street name and number"
                            validations={{ isValidPrimaryAddress: true }}
                            validationErrors={{ isValidPrimaryAddress: 'Is there another address you can give us? Legally, we can’t establish your account with a PO Box.' }}
                            className={formStyles.FormFieldInSection}
                            value={formData.donorAddress || ''}
                            maxLength={255}
                            testId="personalInformationForm__donorAddress__field"
                            required={false} />
                          <TextFormField
                            name="donorCity"
                            label="Billing City"
                            placeholder="City"
                            className={formStyles.FormFieldInSection}
                            maxLength={40}
                            validations={cityValidation.rules}
                            validationErrors={cityValidation.messages}
                            value={formData.donorCity || ''}
                            testId="personalInformationForm__donorCity__field"
                            required={false} />
                          {!isMobile &&
                          <TextFormField
                            name="donorZipCode"
                            placeholder="Zipcode"
                            maxLength={20}
                            validations={postalCodeValidation.rules}
                            validationErrors={postalCodeValidation.messages}
                            value={formData.donorZipCode || ''}
                            testId="personalInformationForm__donorZipCode__field"
                            required={false} />}
                        </div>
                        <div>
                          <TextFormField
                            name="donorState"
                            label="State"
                            placeholder="State"
                            className={formStyles.FormFieldInSection}
                            maxLength={80}
                            validations={stateValidation.rules}
                            validationErrors={stateValidation.messages}
                            value={formData.donorState || ''}
                            testId="personalInformationForm__donorState__field"
                            required={false} />
                          {isMobile &&
                          <TextFormField
                            className={formStyles.FormFieldInSection}
                            name="donorZipCode"
                            placeholder="Zipcode"
                            maxLength={20}
                            validations={postalCodeValidation.rules}
                            validationErrors={postalCodeValidation.messages}
                            value={formData.donorZipCode || ''}
                            testId="personalInformationForm__donorZipCode__field"
                            required={false} />}
                          <SelectDropdownFormField
                            name="donorCountry"
                            label="Country"
                            className={formStyles.FormField}
                            options={countryList}
                            value={formData.donorCountry || 'United States'}
                            testId="personalInformationForm__donorCountry__field"
                            required={false} />
                        </div>
                      </div>
                    </div>
                    <div className={formStyles.formSection}>
                      <div className={formStyles.twoColumn}>
                        <div>
                          <p className={formStyles.heading}>Phone</p>
                          <MaskedFormField
                            type="tel"
                            className={formStyles.FormField}
                            name="donorPhone"
                            label="Phone Number"
                            placeholder="555-666-7777"
                            mask="phoneNumber"
                            minLength={10}
                            required={false}
                            validations={phoneNumberValidation.rules}
                            validationErrors={phoneNumberValidation.messages}
                            value={initialData.donorPhone || ''} />
                        </div>
                        <div>
                          <p className={formStyles.heading}>Email</p>
                          <TextFormField
                            type="tel"
                            className={formStyles.FormField}
                            name="donorEmail"
                            maxLength={80}
                            label=""
                            placeholder="name@email.com"
                            required={true}
                            validations="isEmail"
                            validationErrors={{
                              isEmail: 'Hmm, that’s not a valid email address.'
                            }}
                            value={initialData.donorEmail || ''} />
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className={formStyles.heading}>Occupation</p>
                      <TextFormField
                        className={formStyles.FormField}
                        name="donorOccupation"
                        maxLength={80}
                        label=""
                        placeholder="Enter occupation"
                        required={true}
                        testId="accountDetails__donorOccupation__field"
                        value={formData.donorOccupation} />
                    </div>
                  </div>
              }
              {
                <div className={styles.donorAdvisedFund}>
                  <TextFormField
                    label="Donor Advised Fund Name "
                    name={donorAdvisedFundName}
                    maxLength={80}
                    className={formStyles.FormField}
                    placeholder="Donor Advised Fund Name "
                    validations={fundNameValidation.rules}
                    validationErrors={fundNameValidation.messages}
                    value={formData[donorAdvisedFundName]}
                    required={true}
                    testId="sepPlanServiceRequirementdetails" />
                  <p className={styles.charityDisclaimer}>
                    When granting to charity, the donor will have the option to include this account
                    name in the letter that accompanies the check.
                    Please use the word “Fund” or “Account” in
                    the name (i.e. ABC Family Charitable Fund). We cannot accept names
                    that include the word “Trust”.
                  </p>
                </div>
              }
            </div>
        }
        {
          hasCustodian &&
          <div className={styles.paperlessDocumentEnrollment}>
            <p className={styles.heading}>Minor information</p>
            <p className={styles.question}>Please enter information of minor.</p>
            <div>
              <TextFormField
                name="minorFirstName"
                label="First Name"
                className={styles.FormFieldInSection}
                placeholder="First Name"
                validations={firstNameValidation.rules}
                validationErrors={firstNameValidation.messages}
                maxLength={35}
                value={formData.minorFirstName || ''}
                required={true}
                testId="accountDetails__minorFirstName__field" />

              <TextFormField
                name="minorMiddleName"
                label="Middle Name"
                className={styles.FormFieldInSection}
                placeholder="Middle Name"
                validations={middleNameValidation.rules}
                validationErrors={middleNameValidation.messages}
                maxLength={35}
                value={formData.minorMiddleName || ''}
                required={false}
                testId="accountDetails__minorMiddleName__field" />

              <TextFormField
                name="minorLastName"
                label="Last Name"
                className={styles.FormField}
                maxLength={35}
                placeholder="Last name"
                validations={lastNameValidation.rules}
                validationErrors={lastNameValidation.messages}
                value={formData.minorLastName || ''}
                testId="accountDetails__minorLastName__field"
                required={true} />
            </div>
            <div>
              <div className={inheritedIRASectionStyle}>
                <MaskedFormField
                  name="minorDateOfBirth"
                  label="Date of Birth"
                  value={formData.minorDateOfBirth}
                  validations={birthdateValidation.rules}
                  className={styles.FormField}
                  validationErrors={birthdateValidation.messages}
                  placeholder="MM/DD/YYYY"
                  mask="date"
                  testId="accountDetails__minorDateOfBirth__field"
                  required={true}
                  onChange={value => this.handleBirthdateChange(value, 'minorDateOfBirth')} />
                {(didBirthdateChange || !formData.minorDateOfBirth) &&
                <MaskedFormField
                  name="minorDateOfBirthConfirmation"
                  label="Date of Birth Confirmation"
                  value={formData.minorDateOfBirthConfirmation}
                  validations={didBirthdateChange ? minorDobConfirmationValidation.rules : {}}
                  className={styles.FormField}
                  validationErrors={minorDobConfirmationValidation.messages}
                  placeholder="Confirm MM/DD/YYYY"
                  mask="date"
                  testId="accountDetails__minorDateOfBirthConfirmation__field" />}
              </div>
            </div>
            <div className={inheritedIRASectionStyle}>
              <MaskedFormField
                name="minorSsn"
                label="Social Security Number"
                placeholder="123-45-6789"
                className={styles.FormField}
                validations={isSSNMasked ? undefined : { isValidSocialSecurityNumber: true }}
                validationErrors={isSSNMasked ? undefined : ssnValidation.messages}
                testId="accountDetails__minorSsn__field"
                required={false}
                value={isSSNMasked ? lastFourDigitSsn : ''}
                mask={isSSNMasked ? MASK_PATTERN : 'ssn'}
                onBlur={() => this.handleSsnBlur('minorSsn')}
                onFocus={this.handleSsnFocus}
                onChange={value => this.handleSsnChange(value, 'minorSsn')} />
              {(didSsnChange || !lastFourDigitSsn) &&
                <MaskedFormField
                  name="minorSsnConfirmation"
                  label="SSN Confirmation"
                  placeholder="Confirm SSN"
                  className={styles.FormField}
                  validations={{ isValidMinorSsnMatch: isSsnBeingEdited }}
                  mask={isSSNMasked ? MASK_PATTERN : 'ssn'}
                  validationErrors={minorSsnConfirmationValidation.messages}
                  testId="accountDetails__minorSsnConfirmation__field"
                  required={isSsnBeingEdited}  />
              }
            </div>
            <div>
              <NumberFormField
                name="minorAgeOfTermination"
                label="Age of termination"
                value={formData.minorAgeOfTermination}
                min={18}
                max={25}
                allowDecimal={false}
                className={styles.FormField}
                placeholder="0"
                mask="date"
                testId="personalInformationForm__minorAgeOfTermination__field"
                required={false} />
            </div>
            <div className={styles.disclaimerWrapper}>
              <p className={styles.disclaimerText}>
                Indicate the age for termination of custodianship and the state law under which this Custodial Account will be governed.*
              </p>

              <h6>Age of Termination</h6>

              <p className={styles.disclaimerText}>
                *This is required for minors who are residents of states governed by the Uniform Transfers to Minors Act or by 
                the Uniform Gifts to Minors Act. The age of custodianship termination varies by state, although many states set 
                the maximum age for termination at 21.
              </p>
              <p className={styles.disclaimerText}> 
                If you do not indicate an age here, the account will be set up using the Custodian’s state of 
                residence and that state’s default age of custodianship termination (either 18 or 21).
              </p>
              <p className={styles.disclaimerText}>
                <strong>
                  I understand that electing to extend custodianship to age 25, if applicable, may cause me to lose my annual exclusion from federal gift tax 
                  and that I should consult with an attorney or tax advisor before making this election. 
                </strong>
              </p>
              <p className={styles.disclaimerText}>
                <i>
                  (Note: Only certain states allow the custodianship to be extended to the minor’s 25th birthday. 
                  This election may be exercised only in those states that specifically provide for it.)
                </i>
              </p>
            </div>
          </div>
        }

        {
          isInheritedIRA && 
          <div className={styles.paperlessDocumentEnrollment}>
            <p className={styles.heading}>Deceased information</p>
            <p className={styles.question}>Please enter information for the deceased.</p>
            <div>
              <TextFormField
                name="deceasedFirstName"
                label="First Name"
                className={styles.FormFieldInSection}
                placeholder="First Name"
                validations={firstNameValidation.rules}
                validationErrors={firstNameValidation.messages}
                maxLength={35}
                value={formData.deceasedFirstName || ''}
                required={true}
                testId="accountDetails__deceasedFirstName__field" />

              <TextFormField
                name="deceasedMiddleName"
                label="Middle Name"
                className={styles.FormFieldInSection}
                placeholder="Middle Name"
                validations={middleNameValidation.rules}
                validationErrors={middleNameValidation.messages}
                maxLength={35}
                value={formData.deceasedMiddleName || ''}
                required={false}
                testId="accountDetails__deceasedMiddleName__field" />

              <TextFormField
                name="deceasedLastName"
                label="Last Name"
                className={styles.FormField}
                maxLength={35}
                placeholder="Last name"
                validations={lastNameValidation.rules}
                validationErrors={lastNameValidation.messages}
                value={formData.deceasedLastName || ''}
                testId="accountDetails__deceasedLastName__field"
                required={true} />
            </div>
            <div>
              <div className={inheritedIRASectionStyle}>
                <MaskedFormField
                  name="deceasedDob"
                  label="Date of Birth"
                  value={formData.deceasedDob}
                  validations={birthdateValidation.rules}
                  className={styles.FormField}
                  validationErrors={birthdateValidation.messages}
                  placeholder="MM/DD/YYYY"
                  mask="date"
                  testId="accountDetails__deceasedBirthdate__field"
                  required={false}
                  onChange={value => this.handleBirthdateChange(value, 'deceasedDob')} />
                {(didBirthdateChange || !formData.deceasedDob) &&
                <MaskedFormField
                  name="dobConfirmation"
                  label="Date of Birth Confirmation"
                  value={formData.dobConfirmation}
                  validations={didBirthdateChange ? deceasedDobConfirmationValidation.rules : {}}
                  className={styles.FormField}
                  validationErrors={deceasedDobConfirmationValidation.messages}
                  placeholder="Confirm MM/DD/YYYY"
                  mask="date"
                  testId="accountDetails__deceasedBirthdateConfirmation__field" />}
              </div>
            </div>
            <div className={inheritedIRASectionStyle}>
              <MaskedFormField
                name="deceasedSsn"
                label="Social Security Number"
                placeholder="123-45-6789"
                className={styles.FormField}
                validations={isSSNMasked ? undefined : { isValidSocialSecurityNumber: true }}
                validationErrors={isSSNMasked ? undefined : ssnValidation.messages}
                testId="accountDetails__deceasedSsn__field"
                required={false}
                value={isSSNMasked ? lastFourDigitSsn : ''}
                mask={isSSNMasked ? MASK_PATTERN : 'ssn'}
                onBlur={() => this.handleSsnBlur('deceasedSsn')}
                onFocus={this.handleSsnFocus}
                onChange={value => this.handleSsnChange(value, 'deceasedSsn')} />
              {(didSsnChange || !lastFourDigitSsn) &&
                <MaskedFormField
                  name="ssnConfirmation"
                  label="SSN Confirmation"
                  placeholder="Confirm SSN"
                  className={styles.FormField}
                  validations={{ isValidDeceasedSsnMatch: isSsnBeingEdited }}
                  mask={isSSNMasked ? MASK_PATTERN : 'ssn'}
                  validationErrors={deceasedSsnConfirmationValidation.messages}
                  testId="accountDetails__deceasedSsnConfirmation__field"
                  required={isSsnBeingEdited}  />
              }
            </div>
            <div>
              <MaskedFormField
                name="deceasedDod"
                label="Date of Death"
                value={formData.deceasedDod}
                validations={dateOfDeathValidation.rules}
                className={styles.FormField}
                validationErrors={dateOfDeathValidation.messages}
                placeholder="MM/DD/YYYY"
                mask="date"
                testId="personalInformationForm__dateOfDeath__field"
                required={false} />
            </div>
          </div>
        }

        {
          /* eslint-disable react/button-has-type */
          <button
            className={primaryButtonClasses}
            type={formValid ? 'submit' : 'button'}
            onClick={onClick} >
            Save and Continue
          </button>
        }
      </div>
    )
  }

}

export default compose(
  makeForm,
  withFormResponseHandler,
)(AccountDetailsForm)



// WEBPACK FOOTER //
// ./src/components/Onboarding/Accounts/AccountDetails/AccountDetailsForm.jsx